import { Box, Grid, Radio, RadioGroup, Stack, Text } from "@chakra-ui/react";
import { logError } from "@verivest/shared-components/src/error-handling/logError";
import { useVeriForm } from "@verivest/shared-components/src/react-hook-form";
import dasherize from "dasherize";
import React, { useEffect, useState } from "react";
import VeriInput from "../../../../../components/general/Input";
import VeriSelect from "../../../../../components/general/Select";
import WizardContainer from "../../../../../components/wizards/Container";
import usePlacementsApi from "../../../../../hooks/usePlacementsApi";
import { getProperty } from "../../../../../utils/utils";
import {
  WizardInvestmentDistributionPreferencesReceiveBank,
  WizardInvestmentDistributionPreferencesReceiveCheck,
} from "../DistributionPreferencesReceive";
import RightPanelInvestment from "../RightPanelInvestment";

const AccountRadio = React.forwardRef(
  ({ type, title, description, ...rest }, ref) => {
    return (
      <Radio
        ref={ref}
        colorScheme="brand"
        alignItems="flex-start"
        size="lg"
        value={type}
        mb="16px"
        {...rest}
      >
        <Box ml="4px">
          <Text fontWeight="700" lineHeight="24px" fontSize="16px" mb="4px">
            {title}
          </Text>
          <Text lineHeight="24px" fontSize="16px" color="text.grey">
            {description}
          </Text>
        </Box>
      </Radio>
    );
  },
);

AccountRadio.displayName = "AccountRadio";

const WizardInvestmentDistributionPreferencesSplitSelection = ({
  onBack,
  backLabel,
  investment,
  onFinish,
  asDrawer = false,
  initialData,
  submitText = "Continue",
  onClose,
  customAnswers,
}) => {
  const [error, setError] = useState();
  const onSubmit = (values) => {
    return new Promise((resolve) => {
      if (values.method1 === "Select" || values.method2 === "Select") {
        return setError("You need to choose both methods");
      }

      if (onFinish) {
        return onFinish(values);
      }
      return resolve();
    });
  };

  const { register, handleSubmit, setValue, formState, watch, getValues } =
    useVeriForm({
      defaultValues:
        initialData && initialData.methodType
          ? {
              methodType: initialData.methodType,
              method1: initialData.method1,
              method2: initialData.method2,
              method1Value: initialData.method1Value,
              method2Value: initialData.method2Value,
            }
          : {
              methodType: "Amount",
            },
    });
  const { errors } = formState;
  const getError = (fieldName) => {
    const e = getProperty(errors || {}, fieldName);
    return e && e.message;
  };

  watch(["method1", "method2"]);
  const watchMethodType = watch("methodType");
  // const watchMethods = watch(["method1", "method2", "methodType"]);
  const watchValue = watch("method1Value");
  const currentValues = getValues();

  useEffect(() => {
    if (currentValues.method1Value !== null) {
      if (initialData && currentValues.methodType === initialData.methodType) {
        setValue("method1Value", initialData.method1Value || "");
        setValue("method2Value", initialData.method2Value || "");
        return;
      }

      setValue("method1Value", null);
      setValue("method2Value", null);
    }
  }, [watchMethodType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentValues.methodType === "Percent") {
      const intValue = parseInt(watchValue);
      if (intValue && intValue > 0 && intValue < 100) {
        setValue("method2Value", 100 - intValue);
      }
    }
  }, [watchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const offering = investment.offering;
  const distributionMethods = [];
  if (offering.allowsBank) distributionMethods.push("Bank Transfer");
  if (offering.allowsCheck) distributionMethods.push("Check");
  if (offering.allowsReinvest) distributionMethods.push("Reinvest");

  return (
    <WizardContainer
      customAnswers={customAnswers}
      title="How would you like to split the distributions?"
      onSubmit={handleSubmit(onSubmit)}
      rightExtra={!asDrawer && <RightPanelInvestment investment={investment} />}
      message={error}
      onBack={onBack}
      asDrawer={asDrawer}
      backLabel={backLabel}
      submitText={submitText}
      onClose={onClose}
    >
      <RadioGroup defaultValue="Amount" value={currentValues.methodType}>
        <Stack spacing={4}>
          <AccountRadio
            {...register("methodType", {
              required: {
                value: true,
              },
            })}
            description={
              "Splitting a distribution by amount will distribute up to the amount of the first method, and then automatically apply the rest of the distribution to the second method."
            }
            type="Amount"
            title="Amount"
          />
          <AccountRadio
            {...register("methodType", {
              required: {
                value: true,
              },
            })}
            description={
              "Splitting a distribution by percent will split the distribution amounts based on percentage."
            }
            type="Percent"
            title="Percentage"
          />
        </Stack>
      </RadioGroup>
      <Grid
        templateColumns={{ base: "1fr", md: "1fr 1fr" }}
        mt="32px"
        gap="24px"
      >
        <VeriSelect
          label="First distribution method"
          {...register("method1", { required: true })}
        >
          <option>Select</option>
          {distributionMethods.map((method, i) => {
            if (method === "Reinvest" && currentValues.method2 === "Reinvest")
              return null;
            if (method === "Check" && currentValues.method2 === "Check")
              return null;
            return (
              <option key={i} value={method}>
                {method}
              </option>
            );
          })}
        </VeriSelect>

        {currentValues.methodType === "Amount" && (
          <VeriInput
            {...register("method1Value", {
              required: "First distribution method value is required",
              pattern: {
                value: /^\d+$/,
                message: "You must enter an amount in dollars, example: 500",
              },
            })}
            inputLeft="$"
            label="First distribution method value"
            helperText="Enter an amount in dollars, example: 500"
            error={getError("method1Value")}
          />
        )}
        {currentValues.methodType === "Percent" && (
          <VeriInput
            {...register("method1Value", {
              required: "First distribution method value is required",
              pattern: {
                value: /^(\d?[1-9]|[1-9]0)$/,
                message: "You must enter a percentage between 1 and 99",
              },
            })}
            inputRight="%"
            label="Percentage"
            helperText="Enter a percentage between 1 and 99"
            error={getError("method1Value")}
          />
        )}
        <VeriSelect
          label="Second distribution method"
          {...register("method2", { required: true })}
        >
          <option>Select</option>
          {distributionMethods.map((method, i) => {
            if (method === "Reinvest" && currentValues.method1 === "Reinvest")
              return null;
            if (method === "Check" && currentValues.method1 === "Check")
              return null;
            return (
              <option key={i} value={method}>
                {method}
              </option>
            );
          })}
        </VeriSelect>
        {currentValues.methodType === "Amount" && (
          <VeriInput
            propsInput={{ isReadOnly: true }}
            {...register("method2Value")}
            label="Second distribution method value"
            helperText="The remainder amount will be automatically calculated"
            error={getError("method2Value")}
          />
        )}
        {currentValues.methodType === "Percent" && (
          <VeriInput
            propsInput={{ isReadOnly: true }}
            {...register("method2Value", {
              pattern: {
                value: /^(\d?[1-9]|[1-9]0)$/,
                message: "You must enter a percentage between 1 and 99",
              },
            })}
            inputRight="%"
            label="Percentage"
            helperText="The remaining percentage will be automatically calculated"
            error={getError("method2Value")}
          />
        )}
      </Grid>
    </WizardContainer>
  );
};

const SplitDistribution = ({
  basePath,
  investment,
  distributionPreference,
  asDrawer = false,
  onFinish,
  onBackToTypeSelection,
  initialData,
  onClose,
  customAnswers,
  wizardInvestmentDistributionPreferencesReceiveBankProps,
  wizardInvestmentDistributionPreferencesReceiveSelectionProps,
}) => {
  const [state, setState] = useState({
    data: false,
    steps: [],
  });
  const placementsApi = usePlacementsApi();

  useEffect(() => {
    if (state.data && state.steps.length === 0) {
      const finalize = async () => {
        try {
          if (distributionPreference && distributionPreference.id) {
            const distData = dasherize({
              ...state.data,
              id: distributionPreference.id,
              distributionMethod: "Split Distribution",
              method1Value: parseInt(state.data.method1Value),
              method2Value:
                state.data.method2Value && state.data.method2Value !== ""
                  ? parseInt(state.data.method2Value)
                  : 0,
            });
            await placementsApi.updateResource(
              "distribution-preference",
              distData,
            );
          } else {
            const distData = dasherize({
              ...state.data,
              distributionMethod: "Split Distribution",
              method1Value: parseInt(state.data.method1Value),
              method2Value:
                state.data.method2Value && state.data.method2Value !== ""
                  ? parseInt(state.data.method2Value)
                  : 0,
              account: { id: investment.account.id },
              offering: { id: investment.offering.id },
            });
            await placementsApi.createResource(
              "distribution-preference",
              distData,
            );
          }

          onFinish && onFinish();
        } catch (e) {
          logError(e);
          throw e;
        }
      };

      finalize();
    }
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  let props = {
    distributionPreference: distributionPreference,
    investment: investment,
    basePath: basePath,
    backLabel:
      (!state.data || state.completedSelection !== true) &&
      "Change distribution type",
    onBack: () => {
      if (
        onBackToTypeSelection &&
        (!state.data || state.completedSelection !== true)
      ) {
        onBackToTypeSelection();
        return;
      }

      setState((d) => ({ ...d, completedSelection: false }));
    },
    submitText:
      asDrawer && state.data && state.steps.length === 1 ? "Save" : "Continue",
    onClose,
    customAnswers: customAnswers,
    asDrawer,
  };

  if (!state.data || state.completedSelection !== true) {
    return (
      <WizardInvestmentDistributionPreferencesSplitSelection
        {...props}
        onFinish={(data) => {
          setState((state) => {
            let steps = [];
            if (data.method1 !== "Reinvest") steps.push(data.method1);
            if (data.method2 !== "Reinvest") steps.push(data.method2);
            return {
              ...state,
              data: data,
              steps: steps,
              completedSelection: true,
            };
          });
        }}
        initialData={initialData}
      />
    );
  }

  if (state.data && state.steps.length > 0) {
    const step = state.steps[0];
    if (step === "Check") {
      return (
        <WizardInvestmentDistributionPreferencesReceiveCheck
          {...wizardInvestmentDistributionPreferencesReceiveSelectionProps}
          {...props}
          onFinish={(address, type) => {
            return setState((d) => {
              const steps = [...d.steps];
              steps.shift();
              return {
                ...d,
                data: {
                  ...d.data,
                  addressType: type,
                  distributionAddress: { id: address.id },
                },
                steps: steps,
              };
            });
          }}
        />
      );
    } else if (step === "Bank Transfer") {
      return (
        <WizardInvestmentDistributionPreferencesReceiveBank
          {...wizardInvestmentDistributionPreferencesReceiveBankProps}
          {...props}
          onFinish={(bankAccount) => {
            // by default we assume that first we process bankAccount for first method,
            // so when method2 bank account is selected, we have bankAccount populated,
            // so we use bankAccount2
            let bankIndex = state.data.bankAccount ? "2" : "";
            return setState((d) => {
              const steps = [...d.steps];

              // but even if bankAccount is not populated, for method2 which is a bank transfer,
              // we have to use bankAccount2
              if (
                d.data.method2 == "Bank Transfer" &&
                d.data.method1 !== "Bank Transfer"
              ) {
                bankIndex = "2";
              }
              steps.shift();
              return {
                ...d,
                data: {
                  ...d.data,
                  [`bankAccount${bankIndex}`]: { id: bankAccount.id },
                },
                steps: steps,
              };
            });
          }}
        />
      );
    }
  }

  return <></>;
};

export default SplitDistribution;
